import React, { useState, useEffect } from "react";
import { toast } from "react-toastify";
import { getCampaignDetail, getCampaignReport } from "../../services/campaignService";

const labelStyle = { fontSize: "12px", color: "#6B7280", marginBottom: "2px" };
const valueStyle = { fontSize: "14px", color: "#111827", fontWeight: "600" };

const STATUS_COLORS = {
  DRAFT: { bg: "#F3F4F6", color: "#374151" },
  SCHEDULED: { bg: "#EFF6FF", color: "#2563EB" },
  QUEUED: { bg: "#F5F3FF", color: "#7C3AED" },
  RUNNING: { bg: "#FFFBEB", color: "#B45309" },
  COMPLETED: { bg: "#E6F9F0", color: "#27AE60" },
  PARTIAL_SUCCESS: { bg: "#FFF7ED", color: "#C2410C" },
  FAILED: { bg: "#FFF3F3", color: "#C0335A" },
  CANCELLED: { bg: "#F3F4F6", color: "#6B7280" },
};

const StatusPill = ({ status }) => {
  const c = STATUS_COLORS[status] || STATUS_COLORS.DRAFT;
  return (
    <span style={{ display: "inline-flex", padding: "3px 12px", borderRadius: "9999px", fontSize: "12px", fontWeight: "600", background: c.bg, color: c.color }}>
      {String(status || "").replace("_", " ")}
    </span>
  );
};

const StatTile = ({ label, value, color }) => (
  <div style={{ flex: 1, minWidth: "90px", background: "#F9FAFB", borderRadius: "10px", padding: "12px", textAlign: "center" }}>
    <div style={{ fontSize: "20px", fontWeight: "700", color: color || "#111827" }}>{value ?? 0}</div>
    <div style={{ fontSize: "12px", color: "#6B7280", marginTop: "2px" }}>{label}</div>
  </div>
);

const formatDateTime = (d) => {
  if (!d) return "-";
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? "-" : dt.toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

const CampaignDetailDrawer = ({ campaignId, onClose }) => {
  const [campaign, setCampaign] = useState(null);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const [detailRes, reportRes] = await Promise.all([
          getCampaignDetail(campaignId),
          getCampaignReport(campaignId, { page: 1, limit: 50 }),
        ]);
        if (cancelled) return;
        if (detailRes.success) setCampaign(detailRes.data);
        if (reportRes.success) setReport(reportRes.data);
      } catch (err) {
        if (!cancelled) toast.error("Failed to load campaign details");
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [campaignId]);

  const counts = report?.counts || { PENDING: 0, SENT: 0, FAILED: 0, SKIPPED: 0 };

  let templateParams = [];
  try {
    templateParams =
      typeof campaign?.whatsapp_template_params === "string"
        ? JSON.parse(campaign.whatsapp_template_params || "[]")
        : campaign?.whatsapp_template_params || [];
  } catch {
    templateParams = [];
  }

  return (
    <>
      <div onClick={onClose} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.35)", zIndex: 1040 }} />
      <div
        style={{
          position: "fixed",
          top: 0,
          right: 0,
          bottom: 0,
          width: "min(520px, 100vw)",
          background: "#fff",
          boxShadow: "-4px 0 24px rgba(0,0,0,0.12)",
          zIndex: 1050,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "20px 24px",
            borderBottom: "1px solid #F3F4F6",
            position: "sticky",
            top: 0,
            background: "#fff",
            zIndex: 1,
          }}
        >
          <h2 style={{ margin: 0, fontSize: "18px", fontWeight: "700", color: "#111827" }}>Campaign Details</h2>
          <button
            onClick={onClose}
            style={{ background: "none", border: "1px solid #E5E7EB", borderRadius: "8px", width: "36px", height: "36px", fontSize: "18px", cursor: "pointer", color: "#6B7280", display: "flex", alignItems: "center", justifyContent: "center" }}
          >
            ✕
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "24px", display: "flex", flexDirection: "column", gap: "20px" }}>
          {loading || !campaign ? (
            <div style={{ color: "#6B7280", fontSize: "14px" }}>Loading…</div>
          ) : (
            <>
              <div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "8px" }}>
                  <div style={{ fontSize: "17px", fontWeight: "700", color: "#111827" }}>{campaign.name}</div>
                  <StatusPill status={campaign.status} />
                </div>
                <div style={{ display: "flex", gap: "16px", fontSize: "13px", color: "#6B7280" }}>
                  <span>{campaign.channel === "SMS" ? "SMS" : "WhatsApp"}</span>
                  <span>Created {formatDateTime(campaign.created_at)}</span>
                </div>
              </div>

              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "12px" }}>
                <div>
                  <div style={labelStyle}>Audience</div>
                  <div style={valueStyle}>
                    {campaign.audience_type === "ALL_CUSTOMERS" ? "All Customers" : "Uploaded List"} ({campaign.recipient_count ?? 0})
                  </div>
                </div>
                <div>
                  <div style={labelStyle}>{campaign.send_mode === "SCHEDULED" ? "Scheduled For" : "Send Mode"}</div>
                  <div style={valueStyle}>{campaign.send_mode === "SCHEDULED" ? formatDateTime(campaign.scheduled_at) : "Immediate"}</div>
                </div>
                {campaign.started_at && (
                  <div>
                    <div style={labelStyle}>Started</div>
                    <div style={valueStyle}>{formatDateTime(campaign.started_at)}</div>
                  </div>
                )}
                {campaign.completed_at && (
                  <div>
                    <div style={labelStyle}>Completed</div>
                    <div style={valueStyle}>{formatDateTime(campaign.completed_at)}</div>
                  </div>
                )}
              </div>

              <div>
                <div style={labelStyle}>{campaign.channel === "SMS" ? "Message" : "Template"}</div>
                {campaign.channel === "SMS" ? (
                  <div style={{ marginTop: "6px", padding: "10px 14px", background: "#F9FAFB", borderRadius: "8px", fontSize: "14px", color: "#374151", whiteSpace: "pre-wrap" }}>
                    {campaign.body}
                  </div>
                ) : (
                  <div style={{ marginTop: "6px", padding: "10px 14px", background: "#F9FAFB", borderRadius: "8px", fontSize: "14px", color: "#374151" }}>
                    <div style={{ fontWeight: "600" }}>{campaign.whatsapp_template_name}</div>
                    {templateParams.length > 0 && (
                      <div style={{ marginTop: "6px", fontSize: "13px", color: "#6B7280" }}>
                        Variables: {templateParams.join(", ")}
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div>
                <div style={{ ...labelStyle, marginBottom: "8px" }}>Delivery</div>
                <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
                  <StatTile label="Sent" value={counts.SENT} color="#27AE60" />
                  <StatTile label="Failed" value={counts.FAILED} color="#C0335A" />
                  <StatTile label="Pending" value={counts.PENDING} color="#B45309" />
                </div>
              </div>

              <div>
                <div style={{ ...labelStyle, marginBottom: "8px" }}>Recipients</div>
                <div style={{ border: "1px solid #F3F4F6", borderRadius: "8px", overflow: "hidden" }}>
                  <table style={{ width: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ background: "#F9FAFB" }}>
                        <th style={{ textAlign: "left", padding: "8px 12px", fontSize: "12px", color: "#6B7280", fontWeight: "600" }}>Phone</th>
                        <th style={{ textAlign: "left", padding: "8px 12px", fontSize: "12px", color: "#6B7280", fontWeight: "600" }}>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(report?.recipients || []).length === 0 ? (
                        <tr>
                          <td colSpan={2} style={{ padding: "16px", textAlign: "center", color: "#9CA3AF", fontSize: "13px" }}>
                            No recipients yet
                          </td>
                        </tr>
                      ) : (
                        report.recipients.map((r) => (
                          <tr key={r.id} style={{ borderTop: "1px solid #F3F4F6" }}>
                            <td style={{ padding: "8px 12px", fontSize: "13px", color: "#374151" }}>{r.phone_number}</td>
                            <td style={{ padding: "8px 12px", fontSize: "13px" }}>
                              <span style={{ color: r.status === "SENT" ? "#27AE60" : r.status === "FAILED" ? "#C0335A" : "#B45309", fontWeight: "600" }}>
                                {r.status}
                              </span>
                              {r.error_message && <div style={{ fontSize: "11px", color: "#9CA3AF" }}>{r.error_message}</div>}
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default CampaignDetailDrawer;
